import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CalendarPlus, CalendarX, CalendarClock } from "lucide-react";
import { api } from "../../lib/api";

const ICONS = { booking: CalendarPlus, cancel: CalendarX, reschedule: CalendarClock };

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/admin/notifications").then(({ data }) => {
      setItems(data.items);
      setUnread(data.unread_count);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    await api.post(`/admin/notifications/${id}/read`).catch(() => {});
    load();
  };
  const markAll = async () => {
    await api.post(`/admin/notifications/read-all`).catch(() => {});
    load();
  };
  const openItem = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    nav("/admin/agenda");
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="relative p-2 rounded-full hover:bg-brand-blush transition-colors" data-testid="notification-bell" aria-label="Notificaties">
        <Bell size={20} className="text-brand-charcoal" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-pinkhover text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[85vw] bg-white rounded-2xl border border-brand-border shadow-lg overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border">
            <span className="font-display text-lg font-semibold text-brand-charcoal">Notificaties</span>
            {unread > 0 && <button onClick={markAll} className="text-xs text-brand-pinkhover font-medium" data-testid="notification-read-all">Alles gelezen</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-sm text-brand-muted text-center py-8">Nog geen meldingen.</p>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`} className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-brand-border/60 hover:bg-brand-blush transition-colors ${n.read ? "opacity-60" : ""}`}>
                    <span className="h-8 w-8 rounded-full bg-brand-pink/20 flex items-center justify-center shrink-0 mt-0.5"><Icon size={15} className="text-brand-pinkhover" /></span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-charcoal flex items-center gap-1.5">
                        {!n.read && <span className="h-2 w-2 rounded-full bg-brand-pinkhover shrink-0" />}{n.title}
                      </p>
                      <p className="text-sm text-brand-muted leading-snug">{n.message}</p>
                    </div>
                    {!n.read && <Check size={15} className="text-brand-border ml-auto shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
